import { Copy07Icon } from '@/components/icons/Copy07Icon';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import {
  MenuItem,
  MenuItemProps,
} from '@/components/poppers/MenuPopper/MenuItem';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';

import { DocumentsTableRowData } from '../DocumentsTable.types';
import { useCopyDocumentTextActionItemLazyQuery } from './graphql/CopyDocumentTextActionItem.generated';

interface CopyDocumentTextActionItemProps extends Partial<MenuItemProps> {
  row: DocumentsTableRowData;
}

/**
 * @description This an action item component for use by the table ActionButtonRenderer
 */
export function CopyDocumentTextActionItem({
  row,
  ...menuItemProps
}: CopyDocumentTextActionItemProps) {
  const [copyToClipboard] = useCopyToClipboard();
  const { createErrorFeedback, showFeedback } = useFeedback();
  const [copyDocumentText, { loading }] =
    useCopyDocumentTextActionItemLazyQuery({
      variables: {
        documentId: row.id,
      },
      onError: createErrorFeedback('Failed to copy document text'),
      onCompleted: (data) => {
        // typeguard
        if (data?.node?.__typename !== 'Document') return;

        // protect against un-ocr'd docs
        if (!data.node.annotatedDocumentContent) {
          return showFeedback(`No annotated content found`);
        }

        return copyToClipboard(
          data.node.annotatedDocumentContent,
          () =>
            showFeedback('Annotated text copied to clipboard', {
              variant: 'success',
            }),
          () => showFeedback('Failed to copy to clipboard')
        );
      },
    });

  return (
    <MenuItem
      icon={<Copy07Icon size={18} />}
      loading={loading}
      label="Copy to clipboard"
      onClick={() => copyDocumentText()}
      {...menuItemProps}
    />
  );
}
